// Default 8 change items for MapYourMeds system updates
export const DEFAULT_CHANGELOG_DATA = [
  {
    id: 'cl-8',
    version: 'v2.8.0',
    date: 'August 2026',
    tag: 'New Feature',
    tagVariant: 'success',
    title: 'Enhanced eMAR & Real-Time MedPass Workflow',
    description: 'Upgraded the electronic medication administration record (eMAR) with real-time verification indicators, dosage administration notes, and instant color-coded visual alerts for missed or upcoming doses during daily caregiver rounds.',
    highlights: [
      'Visual badge indicators for verified vs. unverified prescriptions',
      'One-click MedPass administration confirmation with audit logging',
      'Automated time-window alerts for scheduled medication rounds'
    ]
  },
  {
    id: 'cl-7',
    version: 'v2.7.4',
    date: 'August 2026',
    tag: 'Improvement',
    tagVariant: 'primary',
    title: 'Route4Me Automated Delivery & Dispatch Tracking',
    description: 'Seamless integration with Route4Me allowing automatic driver assignment, real-time route optimization, live delivery ETA calculations, and digital proof-of-delivery capture directly from the delivery dashboard.',
    highlights: [
      'Dynamic dispatch status synchronization between pharmacy and facility',
      'Proof-of-delivery signature and photo attachment viewer',
      'Optimized multi-stop delivery driver route assignment'
    ]
  },
  {
    id: 'cl-6',
    version: 'v2.7.0',
    date: 'July 2026',
    tag: 'New Feature',
    tagVariant: 'success',
    title: 'SharedLiving Remote Caregiver & Patient Portal',
    description: 'Introduced a dedicated SharedLiving module for remote caregivers and patient coordinators. Enables personalized at-home care tracking, patient assignment management, and automated daily care summary reports.',
    highlights: [
      'Dedicated caregiver dashboard with scoped patient visibility',
      'Self-contained caregiver reports and medication checklist',
      'Multi-group permission controls tailored for SharedLiving programs'
    ]
  },
  {
    id: 'cl-5',
    version: 'v2.6.2',
    date: 'July 2026',
    tag: 'Improvement',
    tagVariant: 'primary',
    title: 'Automated Refill Reminders & Queue Management',
    description: 'Facility staff can now review upcoming medication depletion dates with threshold-based refill alerts, 1-click batch refill approvals, and instant pharmacy communication for non-cycle refill requests.',
    highlights: [
      'Batch refill request submissions with urgent/regular priority tags',
      'Direct order tracking from refill request to pharmacy fulfillment',
      'Refill reminder dashboard widget with filterable date ranges'
    ]
  },
  {
    id: 'cl-4',
    version: 'v2.5.5',
    date: 'June 2026',
    tag: 'Security',
    tagVariant: 'warning',
    title: 'HIPAA Compliance & Active Session Audit Logs',
    description: 'Strengthened system security and HIPAA compliance with active session monitoring, IP access tracking, automatic idle timeouts, and granular audit trail logging for all sensitive PHI record interactions.',
    highlights: [
      'Active session manager with remote logout capabilities',
      'Comprehensive HIPAA access history logs with timestamps and IP addresses',
      'Updated mandatory HIPAA compliance acknowledgement modal'
    ]
  },
  {
    id: 'cl-3',
    version: 'v2.5.0',
    date: 'June 2026',
    tag: 'New Feature',
    tagVariant: 'success',
    title: 'Day Program Bulk Patient Import & Rosters',
    description: 'Facility administrators can now bulk import Day Program patient rosters via CSV templates, automatically reconcile duplicate entries, and configure specialized day program medication schedules.',
    highlights: [
      'CSV upload wizard with validation and error reporting',
      'Dedicated Day Program patient schedule view and med passes',
      'Customized attendance and medication coordination tools'
    ]
  },
  {
    id: 'cl-2',
    version: 'v2.4.2',
    date: 'May 2026',
    tag: 'Improvement',
    tagVariant: 'info',
    title: 'Custom MedSheet & SplitMed PDF Reports',
    description: 'Redesigned the reporting engine to generate high-resolution, printable MedSheets, LOA forms, Treatment sheets, and SplitMed reports with barcode headers and customizable facility layouts.',
    highlights: [
      'Multi-format MedSheet PDF export options',
      'Barcode integration for physical document scanning',
      'Blank Medsheet generator for emergency/offline use'
    ]
  },
  {
    id: 'cl-1',
    version: 'v2.3.8',
    date: 'May 2026',
    tag: 'Performance',
    tagVariant: 'secondary',
    title: 'Pharmacy Drug Catalog & NDC Lookup Acceleration',
    description: 'Enhanced drug search indexing to accelerate NDC code lookups by over 60%, introduced instant auto-complete suggestions, and added real-time drug unit and dosage route validation.',
    highlights: [
      'Instant NDC lookup with smart dosage suggestions',
      'Fast drug-to-pharmacy mapping interface',
      'Reduced initial page load latency across the catalog'
    ]
  }
];

const STORAGE_KEY = 'mapyourmeds_changelog_items';
const EVENT_NAME = 'mapyourmeds_changelog_changed';

/**
 * Get stored changelog items, or initialize with DEFAULT_CHANGELOG_DATA
 */
export const getStoredChangelogs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_CHANGELOG_DATA));
      return DEFAULT_CHANGELOG_DATA;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_CHANGELOG_DATA;
  } catch (err) {
    console.error('Error reading changelogs from localStorage:', err);
    return DEFAULT_CHANGELOG_DATA;
  }
};

/**
 * Save changelog list to localStorage and dispatch update event
 */
export const saveChangelogs = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: list }));
    }
  } catch (err) {
    console.error('Error saving changelogs to localStorage:', err);
  }
};

/**
 * Add a new changelog item to the beginning of the list
 */
export const addChangelogItem = (item) => {
  const currentList = getStoredChangelogs();
  const newItem = {
    id: `cl-${Date.now()}`,
    date: item.date || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    tagVariant: getTagVariant(item.tag),
    ...item
  };
  const updatedList = [newItem, ...currentList];
  saveChangelogs(updatedList);
  return updatedList;
};

/**
 * Delete a changelog item by ID
 */
export const deleteChangelogItem = (id) => {
  const currentList = getStoredChangelogs();
  const updatedList = currentList.filter(item => item.id !== id);
  saveChangelogs(updatedList);
  return updatedList;
};

/**
 * Reset changelogs to default 8 items
 */
export const resetChangelogs = () => {
  saveChangelogs(DEFAULT_CHANGELOG_DATA);
  return DEFAULT_CHANGELOG_DATA;
};

/**
 * Helper to determine badge variant by tag
 */
export const getTagVariant = (tag) => {
  switch (tag) {
    case 'New Feature':
      return 'success';
    case 'Improvement':
      return 'primary';
    case 'Security':
      return 'warning';
    case 'Bug Fix':
      return 'danger';
    case 'Performance':
      return 'secondary';
    default:
      return 'info';
  }
};

export { EVENT_NAME };

/* ------------------------------------------------------------------ */
/* Release notes from the login API                                    */
/* ------------------------------------------------------------------ */
// Login response shape:
//   release_notes: [{ id, version, title, body, published_at, created }]
//   latest_release_note_id: number

const RELEASE_NOTES_KEY = 'releaseNotes';
const LATEST_RELEASE_NOTE_ID_KEY = 'latestReleaseNoteId';
const SEEN_RELEASE_NOTE_ID_KEY = 'seenReleaseNoteId';

const BULLET_PATTERN = /^\s*[-*\u2022]\s+/;

const readRawReleaseNotes = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RELEASE_NOTES_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
};

const formatReleaseVersion = (version) => {
  if (!version) return '';
  const value = String(version).trim();
  return /^v/i.test(value) ? value : `v${value}`;
};

const formatReleaseDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Split a release note body into a paragraph + bullet highlights.
 * Lines starting with "-", "*" or "\u2022" become highlights; everything else
 * is kept (line breaks preserved) as the description.
 */
export const parseReleaseNoteBody = (body = '') => {
  const description = [];
  const highlights = [];
  String(body || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      if (BULLET_PATTERN.test(line)) {
        highlights.push(line.replace(BULLET_PATTERN, ''));
      } else {
        description.push(line);
      }
    });
  return { description: description.join('\n'), highlights };
};

/** Map an API release note to the shape ChangelogPopUp renders. */
export const mapReleaseNote = (note) => ({
  id: note.id,
  version: formatReleaseVersion(note.version),
  date: formatReleaseDate(note.published_at || note.created),
  title: note.title || '',
  ...parseReleaseNoteBody(note.body)
});

/** Persist `release_notes` / `latest_release_note_id` from the login response. */
export const storeLoginReleaseNotes = (loginResponse) => {
  try {
    const notes = Array.isArray(loginResponse?.release_notes) ? loginResponse.release_notes : [];
    localStorage.setItem(RELEASE_NOTES_KEY, JSON.stringify(notes));

    const latestId = loginResponse?.latest_release_note_id;
    if (latestId !== undefined && latestId !== null && latestId !== '') {
      localStorage.setItem(LATEST_RELEASE_NOTE_ID_KEY, String(latestId));
    } else {
      localStorage.removeItem(LATEST_RELEASE_NOTE_ID_KEY);
    }
  } catch (err) {
    console.error('Error storing release notes:', err);
  }
};

/** Release notes from login, newest first, mapped for display. */
export const getLoginReleaseNotes = () =>
  [...readRawReleaseNotes()]
    .sort((a, b) => {
      const byDate = new Date(b.published_at || b.created) - new Date(a.published_at || a.created);
      return byDate || (Number(b.id) || 0) - (Number(a.id) || 0);
    })
    .map(mapReleaseNote);

export const getLatestReleaseNoteId = () => {
  const stored = localStorage.getItem(LATEST_RELEASE_NOTE_ID_KEY);
  if (stored) return stored;
  const ids = readRawReleaseNotes().map((note) => Number(note.id) || 0);
  return ids.length ? String(Math.max(...ids)) : '';
};

/** True when login returned notes the user hasn't dismissed this session. */
export const hasUnseenReleaseNotes = () => {
  try {
    if (readRawReleaseNotes().length === 0) return false;
    return localStorage.getItem(SEEN_RELEASE_NOTE_ID_KEY) !== getLatestReleaseNoteId();
  } catch (err) {
    return false;
  }
};

/** Remember that the current latest release note was dismissed. */
export const markReleaseNotesSeen = () => {
  try {
    localStorage.setItem(SEEN_RELEASE_NOTE_ID_KEY, getLatestReleaseNoteId());
  } catch (err) {
    // ignore storage errors
  }
};
